import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, map } from 'lodash-es'
import { FC, useEffect, useState } from 'react'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import * as yup from 'yup'

import { PreviewTemplate } from './PreviewTemplate'
import Loader from '../../../../common/components/Loader'
import {
  ITemplates,
  Languages,
  Template,
  TemplateType,
  Variable,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { HtmlEditor } from '../../../../core/components/HtmlEditor'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getAllEmailTemplates,
  getEmailTemplateById,
  getPreviews,
  updateEmailTemplateById,
} from '../../../../core/services/email-templates'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox } from '@/components/ui'
import SelectField from '@/components/select-field'
import type { SelectOptionGroup } from '@/components/select-field'

interface IFormValues {
  id: string
  name: string
  category: string
  type: TemplateType
  texts: {
    id: string
    key: string
    optional: boolean
    displayable: boolean
    translations?: {
      id: string
      locale: string
      text: string
    }[]
  }[]
}

type EmailTemplateItemProps = {
  control: Control<IFormValues>
  field: FieldArrayWithId<IFormValues, 'texts', 'id'>
  index: number
  watch: UseFormWatch<IFormValues>
  setValue: UseFormSetValue<IFormValues>
  errors: FieldErrors<IFormValues>
  allLanguages: Languages[]
  variables: Variable[]
  disabled: boolean
}

const EmailTemplateItem: FC<EmailTemplateItemProps> = ({
  control,
  errors,
  field,
  index,
  watch,
  setValue,
  allLanguages,
  variables,
  disabled,
}) => {
  const { fields: contentFields } = useFieldArray({
    control,
    name: `texts.${index}.translations`,
  })

  const visible = watch(`texts.${index}.displayable`)
  const optional = watch(`texts.${index}.optional`)

  function camelCaseToWords(s: string) {
    const result = s.replace(/([A-Z])/g, ' $1')
    return result.charAt(0).toUpperCase() + result.slice(1)
  }

  return (
    <div className="mt-4">
      <h3>{camelCaseToWords(field.key)}</h3>
      {optional && (
        <FormGroup
          label="Visible"
          direction="row"
          labelClassName="whitespace-nowrap"
        >
          <Checkbox
            checked={field.displayable}
            onCheckedChange={(checked) => {
              setValue(`texts.${index}.displayable`, Boolean(checked))
            }}
            disabled={disabled}
          />
        </FormGroup>
      )}
      {visible &&
        contentFields.map((contentField, contentIndex) => {
          const html = watch(`texts.${index}.translations.${contentIndex}.text`)
          return (
            <Card
              className="border border-monochrome-120 p-4 mb-3"
              key={contentField.id}
            >
              <div className="flex justify-between mb-3 items-center">
                <h5 className="text-sm font-semibold">
                  Language:{' '}
                  <span className="text-accent-1">
                    {
                      find(
                        allLanguages,
                        (cont) => contentField.locale === cont.locale,
                      )?.name
                    }
                  </span>
                </h5>

                {/* <Button
                  onClick={() => {
                    setShowModal((prev) => ({
                      ...prev,
                      [contentField.locale]: true,
                    }))
                  }}
                  className="btn btn-secondary btn-cancel"
                  disabled={disabled}
                >
                  Insert Variable
                </Button> */}
              </div>

              {/* <Controller
                name={`texts.${index}.translations.${contentIndex}.text`}
                control={control}
                defaultValue=""
                render={({ field }) => {
                  return (
                    <CKEditor
                      editor={Editor}
                      data={html}
                      onReady={(editor) => {
                        setEditorInstance((prev) => ({
                          ...prev,
                          [contentField.locale]: editor,
                        }))
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData()
                        field.onChange(data)
                      }}
                      disabled={disabled}
                    />
                  )
                }}
              />

              <InsertVariableModal
                showModal={showModal[contentField.locale]}
                setShowModal={(show) => {
                  setShowModal((prev) => ({
                    ...prev,
                    [contentField.locale]: show,
                  }))
                }}
                variables={variables}
                setInsertVariable={(val) => {
                  if (editorInstance[contentField.locale]) {
                    editorInstance[contentField.locale].model.change(
                      (writer) => {
                        editorInstance[contentField.locale].model.insertContent(
                          writer.createText(val),
                          editorInstance[contentField.locale].model.document
                            .selection,
                        )
                      },
                    )
                    setShowModal((prev) => ({
                      ...prev,
                      [contentField.locale]: false,
                    }))
                  }
                }}
              /> */}

              <HtmlEditor
                label={'Translation'}
                control={control}
                variables={variables}
                defaultCustomHTML={false}
                isError={
                  !!errors?.texts?.[index]?.translations?.[contentIndex]?.text
                    ?.message
                }
                name={`texts.${index}.translations.${contentIndex}.text`}
                value={html || ''}
                onChange={(data) => {
                  setValue(
                    `texts.${index}.translations.${contentIndex}.text`,
                    data,
                  )
                }}
                disabled={disabled}
                index={contentIndex}
              />
            </Card>
          )
        })}
    </div>
  )
}

interface TemplatesProps {
  setSelectedTemplate: (s: string) => void
  selectedTemplate: string
  allLanguages: Languages[]
  variables: Variable[]
}

export const Templates = (props: TemplatesProps) => {
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })
  const { setSelectedTemplate, selectedTemplate, allLanguages, variables } =
    props
  const [previewTemplate, setPreviewTemplate] = useState<string>('')
  const [isUploading, setIsUploading] = useState<boolean>(true)
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [currentTemplate, setCurrentTemplate] = useState<Template>()
  const [allTemplates, setAllTemplates] = useState<
    { name: string; items: ITemplates[] }[]
  >([])
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const removeDuplicates = (arr: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return arr.filter((item: any, index: number) => arr.indexOf(item) === index)
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const resp = await getAllEmailTemplates()
        if (resp.status === 200) {
          const categories = removeDuplicates(map(resp.data, d => d.category))
          const temp = map(categories, cat => {
            return {
              name: cat,
              items: filter(resp.data, d => d.category === cat)
            }
          })
          setAllTemplates(temp)
          setSelectedTemplate(resp.data[0].id)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [])

  const schema = yup.object().shape({
    id: yup.string().required(),
    name: yup.string().required(),
    category: yup.string().required(),
    type: yup
      .string()
      .oneOf([
        'NEWSLETTER_SUBSCRIPTION_CONFIRMATION',
        'USER_EMAIL_CONFIRMATION',
        'YOUR_TICKETS',
        'PAYMENT_FAILED',
        'REFUND',
        'INVITE_BATCH',
        'INVITE_BATCH_MAX_10_INVITES',
        'INVITE',
        'CHECKIN_SUCCESSFUL_PAYMENT',
        'CONTACT_FORM',
        'CANCEL_CHECKIN',
        'FREE_CHECKIN_REMINDER',
        'PAID_CHECKIN_REMINDER',
        'CONFIRMATION_CODE',
        'TOPUP_SUCCESSFUL_PAYMENT',
        'TOPUP_FAILED_PAYMENT',
        'TOPUP_COINS_EXPIRATION',
        'REFUND_TOPUP',
        'EXTRAS_DELIVERY_SUCCESSFUL_PAYMENT',
        'EXTRAS_DELIVERY_FAILED_PAYMENT',
        'EXTRAS_DELIVERY_AWB_DETAILS',
        'REFERRAL_BONUS',
        'ON_SITE_ORDER_ATTACHMENT',
        'INVITE_TICKET_REDEEM_INFO_ATTACHMENT',
        'NEW_TICKET',
        'INSTALMENTS_PAYMENT_SUCCESS',
        'INSTALMENTS_PAYMENT_FAILED',
        'INSTALMENTS_PAYMENT_REMINDER',
      ])
      .required(),
    texts: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          key: yup.string().required(),
          optional: yup.boolean().required(),
          displayable: yup.boolean().required(),
          translations: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                locale: yup.string().required(),
                text: yup.string().required(),
              }),
            )
            .when('displayable', {
              is: (val: boolean) => val,
              then: (schema) =>
                schema
                  .min(1, 'At least one translation is required')
                  .required(),
              otherwise: (schema) => schema.min(0).optional(),
            }),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
    watch,
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        if (selectedTemplate !== '') {
          const resp = await getEmailTemplateById(selectedTemplate)
          if (resp.status === 200) {
            setCurrentTemplate(resp.data)
            setValue('id', resp.data.id)
            setValue('name', resp.data.name)
            setValue('category', resp.data.category)
            setValue('type', resp.data.type)
            setTimeout(() => {
              setValue('texts', resp.data.texts)
            }, 200)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [selectedTemplate])

  const { fields } = useFieldArray({
    control,
    name: 'texts',
  })

  const submit = async (values: IFormValues) => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const payload: Template = {
          id: values.id,
          name: values.name,
          category: values.category,
          type: values.type,
          texts: map(values.texts, text => {
            return ({
              id: text.id,
              key: text.key,
              optional: text.optional,
              displayable: text.displayable,
              translations: map(text.translations, trans => {
                return ({
                  id: trans.id,
                  locale: trans.locale,
                  text: trans.text,
                })
              })
            })
          })
        }
        const resp = await updateEmailTemplateById(values.id, payload)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }

  const handlePreview = () => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const resp = await getPreviews(selectedTemplate)
        if (resp.status === 200) {
          setPreviewTemplate(`<div
                  style="
                  width: 100%;
                  margin: auto;
                  "
                  >${resp.data}</div>`)
          setShowPreview(true)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }

  if (isUploading) {
    return <Loader />
  }

  const templateGroups: SelectOptionGroup[] = allTemplates.map((template) => ({
    label: template.name,
    options: template.items.map((item) => ({
      value: item.id,
      label: item.name,
    })),
  }))

  return (
    <>
      <Card className="p-4">
        <div className="flex gap-2">
          <SelectField
            outline
            placeholder="Select Template"
            value={selectedTemplate}
            onValueChange={setSelectedTemplate}
            optionGroups={templateGroups}
            wrapperClassName="flex-1"
          />
          <Button variant="secondary" onClick={handlePreview} size="sm">
            Preview Template
          </Button>
        </div>
        <form id="template-form" onSubmit={handleSubmit(submit)}>
          {fields.map((field, index) => (
            <EmailTemplateItem
              control={control}
              field={field}
              key={field.id}
              index={index}
              variables={variables}
              setValue={setValue}
              watch={watch}
              errors={errors}
              allLanguages={allLanguages}
              disabled={!hasEditPermission}
            />
          ))}
          <div className="flex justify-end w-full gap-2 border-t border-monochrome-120 p-4 mt-4">
            <Button
              onClick={() => {
                reset()
              }}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="template-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </form>
      </Card>
      <PreviewTemplate
        title={currentTemplate?.name || ''}
        template={previewTemplate}
        visible={showPreview}
        setVisible={setShowPreview}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
